/**
 * This callback is displayed as a global member.
 * @callback eventCallback
 * @param {object} event Event callback
 * @param {HTMLElement} target Event target
 */

/**
 * Properties for delegating events
 * @typedef {Object} DelegateParams
 * @property {HTMLElement} element - Element to attach the event listener to
 * @property {string} event Event to listen for
 * @property {string} selector Selector to listen for events on
 * @property {eventCallback} callback Event Callback Function
 */
interface DelegateParams<
  TK extends keyof HTMLElementEventMap,
  TE extends HTMLElement
> {
  element: HTMLElement | string;
  selector: string;
  callback: (event: HTMLElementEventMap[TK], target: TE) => any;
  event: TK;
}

type TDelegateCallback = () => void;

/**
 * Attaches an event listener to an ancestor node to listen for events on the
 * children nodes.
 *
 * @param {DelegateParams} params Information about the event listener
 * @returns {TDelegateCallback} A function to remove the attached event listener
 */
export const delegate = <
  TK extends keyof HTMLElementEventMap,
  TE extends HTMLElement
>(
  params: DelegateParams<TK, TE>
): TDelegateCallback => {
  const { element, selector, event, callback } = params;

  const internalCallback = (event: HTMLElementEventMap[TK]) => {
    if (
      !event.target ||
      !(
        event.target instanceof HTMLElement ||
        event.target instanceof SVGElement
      ) ||
      typeof event.target.matches === typeof undefined
    )
      return;

    const target = event.target.matches(selector)
      ? (event.target as TE)
      : event.target.closest<TE>(selector);
    if (!target) return;
    callback(event, target);
  };

  const elem =
    typeof element === 'string' ? document.querySelector<TE>(element) : element;
  if (!elem) return null;

  elem.addEventListener(event, internalCallback);
  return () => {
    if (elem) elem.removeEventListener(event, internalCallback);
  };
};

/**
 *
 * @param {Event} evt - Event to find target from
 * @param {string} selector - Selector string to find target with
 */
export const findTarget = (evt: Event, selector: string) => {
  if (
    !evt.target ||
    !(evt.target instanceof HTMLElement || evt.target instanceof SVGElement) ||
    typeof evt.target.matches === typeof undefined
  )
    return null;

  const target = evt.target.matches(selector)
    ? (evt.target as HTMLElement)
    : evt.target.closest<HTMLElement>(selector);
  return target;
};
